import { type ReactElement } from 'react';

/**
 * Split-screen sign-in (Nora-style): a sign-in rail on the left, an art panel on the right.
 * Presentational — auth is handled by the caller via `onSocial`, which maps to the real Firebase
 * OAuth providers. Social-only: Google is live, Apple is disabled for now. The right panel is a
 * subtle, themeable geometric pattern (placeholder art) that tracks the active theme.
 */
export type SocialProvider = 'google' | 'apple';

export interface LoginProps {
  /** Real OAuth sign-in (cloud sync). */
  onSocial?: (provider: SocialProvider) => void;
  /** Disable the OAuth buttons (e.g. while a sign-in popup is in flight). */
  busy?: boolean;
}

/**
 * The Lapo brand mark — the orbital glyph (shared with the favicon / app icons). Orbit rings track
 * the theme text color via `currentColor`; the three planets carry the live accent so the mark stays
 * on-brand in both light and dark. Geometry matches public/brand/orbit-*.svg (512-space).
 */
function BrandMark(): ReactElement {
  return (
    <svg className="lapo-login-mark" width="34" height="34" viewBox="0 0 512 512" aria-hidden="true">
      <g transform="translate(256,256)">
        <g fill="none" stroke="currentColor" strokeWidth="20" opacity="0.55">
          <circle r="40" />
          <circle r="96" />
          <circle r="152" />
          <circle r="208" />
        </g>
        <g fill="var(--lapo-accent)" stroke="var(--lapo-accent)" strokeWidth="20">
          <circle cx="0" cy="-96" r="28" />
          <circle cx="131.6" cy="76" r="28" />
          <circle cx="-180.1" cy="104" r="28" />
        </g>
      </g>
    </svg>
  );
}

const GoogleIcon = (): ReactElement => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z" />
  </svg>
);

const AppleIcon = (): ReactElement => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.36 12.78c.02 2.5 2.2 3.33 2.22 3.34-.02.06-.35 1.2-1.15 2.37-.69 1.02-1.4 2.03-2.53 2.05-1.1.02-1.46-.65-2.72-.65s-1.65.63-2.7.67c-1.08.04-1.91-1.1-2.6-2.11-1.43-2.07-2.52-5.85-1.05-8.41a4.07 4.07 0 0 1 3.44-2.1c1.07-.02 2.07.72 2.72.72.64 0 1.86-.89 3.14-.76.53.02 2.04.22 3.01 1.62-.08.05-1.8 1.05-1.78 3.14M14.3 4.55c.57-.69.95-1.65.85-2.6-.82.03-1.81.54-2.4 1.23-.52.6-.98 1.58-.86 2.51.91.07 1.84-.46 2.41-1.14" />
  </svg>
);

/** A subtle, themeable geometric pattern — placeholder art for the right panel. */
function GeometricArt(): ReactElement {
  return (
    <div className="lapo-login-art" aria-hidden="true">
      <svg className="lapo-login-art-svg" preserveAspectRatio="xMidYMid slice" viewBox="0 0 600 800">
        <defs>
          <pattern id="lapo-grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M40 0H0V40" fill="none" stroke="currentColor" strokeWidth="1" />
          </pattern>
          <pattern id="lapo-tri" width="120" height="104" patternUnits="userSpaceOnUse">
            <path d="M60 0 120 104 0 104Z" fill="none" stroke="currentColor" strokeWidth="1.25" />
            <path d="M0 0 60 104 120 0" fill="none" stroke="currentColor" strokeWidth="1.25" />
          </pattern>
        </defs>
        <rect width="600" height="800" fill="url(#lapo-grid)" opacity="0.5" />
        <rect width="600" height="800" fill="url(#lapo-tri)" opacity="0.7" />
        <circle cx="300" cy="300" r="220" fill="none" stroke="currentColor" strokeWidth="1.25" opacity="0.6" />
        <circle cx="300" cy="300" r="150" fill="none" stroke="currentColor" strokeWidth="1.25" opacity="0.6" />
        <circle cx="300" cy="300" r="80" fill="none" stroke="currentColor" strokeWidth="1.25" opacity="0.6" />
        {/* Orbiting planets — echo the brand mark; accent-tinted for a spark of color on the panel. */}
        <circle cx="300" cy="220" r="9" fill="var(--lapo-accent)" />
        <circle cx="429.9" cy="375" r="9" fill="var(--lapo-accent)" />
        <circle cx="109.5" cy="410" r="9" fill="var(--lapo-accent)" />
      </svg>
      <span className="lapo-login-art-mark">lapo</span>
    </div>
  );
}

export function Login({ onSocial, busy = false }: LoginProps): ReactElement {
  return (
    <div className="lapo-login" data-testid="login">
      <section className="lapo-login-rail">
        <BrandMark />

        <div className="lapo-login-body">
          <h1 className="lapo-login-title">Lapo</h1>
          <p className="lapo-login-sub">Sign in to continue.</p>

          <div className="lapo-login-social">
            <button
              type="button"
              className="lapo-login-social-btn"
              aria-label="Continue with Google"
              disabled={busy}
              onClick={() => onSocial?.('google')}
            >
              <GoogleIcon /> <span>Continue with Google</span>
            </button>
            {/* Apple sign-in is disabled for now. */}
            <button
              type="button"
              className="lapo-login-social-btn"
              aria-label="Continue with Apple"
              disabled
            >
              <AppleIcon /> <span>Continue with Apple</span>
            </button>
          </div>

          <p className="lapo-login-legal">
            By continuing you acknowledge that you have read and understood, and agree to Lapo&rsquo;s{' '}
            <a href="#terms">Terms &amp; Conditions</a> and <a href="#privacy">Privacy Policy</a>.
          </p>
        </div>

        <footer className="lapo-login-foot">
          <div className="lapo-login-foot-links">
            <a href="#terms">Privacy &amp; Terms</a>
            <a href="#support">Help &amp; support</a>
          </div>
          <span className="lapo-login-foot-copy">&copy; {new Date().getFullYear()} Lapo Inc.</span>
        </footer>
      </section>

      <GeometricArt />
    </div>
  );
}
